import { Badge } from "@/components/ui/badge"
import { Card } from "@/components/ui/card"
import { toProxiedUrl } from "@shared/imageUrl"
import type { Property } from "@shared/schema"
import { Bath, Bed, MapPin, Maximize, Star } from "lucide-react"
import { Link } from "wouter"

interface PropertyCardProps {
  property: Property
  priority?: boolean
  variant?: "vertical" | "horizontal"
}

export function PropertyCard({
  property,
  priority = false,
  variant = "vertical",
}: PropertyCardProps) {
  const primaryImage = property.images?.[0] || property.imageUrl
  const formatPrice = (price: string) => {
    const num = parseFloat(price)
    return new Intl.NumberFormat("en-PH", {
      style: "currency",
      currency: "PHP",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num)
  }

  const getListingBadgeVariant = (listingType: string) => {
    return listingType === "sale" ? "default" : "secondary"
  }

  const formatAddress = () => {
    const parts = []
    if (property.barangay) parts.push(property.barangay)
    if (property.city) parts.push(property.city)
    if (property.location) parts.push(property.location)
    return parts.length > 0 ? parts.join(", ") : "Location not specified"
  }

  const getPropertyUrl = () => {
    const listing = property.listingType === "rent" ? "for-rent" : "for-sale"
    const slug = property.slug || property.id

    if (property.city) {
      const citySlug = property.city
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")

      return `/${listing}/${citySlug}/${slug}`
    }

    return `/${listing}/${slug}`
  }

  // Single quality tier everywhere (auto:good). auto:best vs auto:good was
  // visually indistinguishable in practice but doubled every image's
  // Cloudinary variant count (and therefore transformations/storage).
  const getOptimizedImageUrl = (
    url: string | undefined | null,
    width: number,
  ) => {
    if (!url) return `${window.location.origin}/og-image.jpg`
    if (url.includes("cloudinary.com") && url.includes("/upload/")) {
      const urlWithoutExt = url.replace(/\.(jpg|jpeg|png|webp)$/i, "")
      const transformations = `f_auto,w_${width},c_limit,q_auto:good`
      const transformedUrl = urlWithoutExt.replace(
        "/upload/",
        `/upload/${transformations}/`,
      )
      return toProxiedUrl(transformedUrl)
    }
    return url
  }

  const generateSrcSet = () => {
    // Trimmed to widths that are actually rendered. Vertical cards sit in a
    // grid cell that rarely exceeds ~450px CSS width; horizontal cards cap
    // at 320px per the sizes attr below, so no candidate needs to go past
    // ~640-900px even accounting for 2x DPR screens.
    const widths = variant === "horizontal" ? [320, 480, 640] : [400, 600, 900]
    return widths
      .map((width) => `${getOptimizedImageUrl(primaryImage, width)} ${width}w`)
      .join(", ")
  }

  const imageSizes =
    variant === "horizontal"
      ? "(max-width: 640px) 100vw, 320px"
      : [
          "(max-width: 640px) 100vw",
          "(max-width: 768px) 50vw",
          "(max-width: 1024px) 33.33vw",
          "900px",
        ].join(", ")

  const hasValue = (val: any) => val !== null && val !== undefined && val > 0

  // Horizontal Layout (for Browse page)
  if (variant === "horizontal") {
    return (
      <Card className="overflow-hidden hover:shadow-lg transition-all duration-200 border dark:border-gray-700 hover:border-primary/30 h-auto sm:h-[320px]">
        <Link
          href={getPropertyUrl()}
          className="flex flex-col sm:flex-row group h-full"
          data-testid={`card-property-${property.id}`}
        >
          <div className="relative w-full sm:w-80 h-56 sm:h-full shrink-0 overflow-hidden bg-muted dark:bg-gray-800">
            <img
              src={getOptimizedImageUrl(primaryImage, 640)}
              srcSet={generateSrcSet()}
              sizes={imageSizes}
              alt={property.title}
              title={property.title}
              loading={priority ? "eager" : "lazy"}
              decoding={priority ? "sync" : "async"}
              width={320}
              height={240}
              onError={(e) => {
                e.currentTarget.onerror = null
                e.currentTarget.src = "/og-image.jpg"
              }}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />

            <div className="absolute top-2 left-2 flex flex-col gap-1">
              <Badge
                className={`text-[10px] font-bold px-2 py-0.5 ${
                  property.listingType === "sale"
                    ? "bg-blue-600 hover:bg-blue-700"
                    : "bg-green-600 hover:bg-green-700"
                }`}
                data-testid={`badge-listing-type-${property.id}`}
              >
                FOR {property.listingType.toUpperCase()}
              </Badge>
              {property.featured && (
                <Badge
                  className="bg-orange-500 hover:bg-orange-600 text-[10px] font-bold px-2 py-0.5"
                  data-testid={`badge-featured-${property.id}`}
                >
                  ★ FEATURED
                </Badge>
              )}
            </div>
          </div>

          <div className="flex-1 p-4 flex flex-col min-w-0 dark:bg-gray-950">
            <div className="mb-2">
              <p
                className="text-2xl sm:text-3xl font-bold text-primary"
                data-testid={`badge-price-${property.id}`}
              >
                ₱
                {Number(property.price).toLocaleString("en-US", {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })}
                {property.listingType === "rent" && (
                  <span className="text-sm sm:text-base text-muted-foreground ml-1">
                    /month
                  </span>
                )}
              </p>
            </div>

            <h3
              className="text-lg sm:text-xl font-bold mb-2 group-hover:text-primary transition-colors line-clamp-2 leading-tight dark:text-gray-100"
              data-testid={`text-title-${property.id}`}
            >
              {property.title}
            </h3>

            <div className="flex items-start gap-2 text-muted-foreground mb-2">
              <MapPin className="h-4 w-4 mt-0.5 shrink-0" aria-hidden="true" />
              <p
                className="text-sm line-clamp-1"
                data-testid={`text-location-${property.id}`}
              >
                {formatAddress()}
              </p>
            </div>

            {property.description && (
              <p className="text-sm text-muted-foreground mb-3 line-clamp-2 sm:line-clamp-3 leading-relaxed dark:text-gray-400">
                {property.description}
              </p>
            )}

            <div className="mb-3">
              <Badge
                variant="outline"
                className="capitalize text-xs dark:border-gray-600 dark:text-gray-300"
                data-testid={`badge-type-${property.id}`}
              >
                {property.propertyType}
              </Badge>
            </div>

            <div className="flex-1"></div>

            <div className="flex items-center justify-between flex-wrap gap-2 mt-auto pt-2 border-t dark:border-gray-700 sm:border-t-0">
              <div className="flex items-center gap-4 text-sm flex-wrap">
                {hasValue(property.bedrooms) && (
                  <div
                    className="flex items-center gap-1.5"
                    data-testid={`text-bedrooms-${property.id}`}
                    title={`${property.bedrooms} bedrooms`}
                  >
                    <Bed
                      className="h-4 w-4 text-muted-foreground"
                      aria-hidden="true"
                    />
                    <span className="font-medium dark:text-gray-300">
                      {property.bedrooms}
                    </span>
                  </div>
                )}
                {hasValue(property.bathrooms) && (
                  <div
                    className="flex items-center gap-1.5"
                    data-testid={`text-bathrooms-${property.id}`}
                    title={`${property.bathrooms} bathrooms`}
                  >
                    <Bath
                      className="h-4 w-4 text-muted-foreground"
                      aria-hidden="true"
                    />
                    <span className="font-medium dark:text-gray-300">
                      {property.bathrooms}
                    </span>
                  </div>
                )}
                {property.floorArea && hasValue(property.floorArea) && (
                  <div
                    className="flex items-center gap-1.5"
                    data-testid={`text-floor-area-${property.id}`}
                    title={`${property.floorArea} square meters`}
                  >
                    <Maximize
                      className="h-4 w-4 text-muted-foreground"
                      aria-hidden="true"
                    />
                    <span className="font-medium dark:text-gray-300">
                      {property.floorArea.toLocaleString()} sqm
                    </span>
                  </div>
                )}
              </div>

              <span className="text-primary text-sm font-medium hover:text-primary/80 cursor-pointer whitespace-nowrap">
                View Details →
              </span>
            </div>
          </div>
        </Link>
      </Card>
    )
  }

  // Vertical Layout (for Home page) - Original design
  return (
    <Link href={getPropertyUrl()} data-testid={`card-property-${property.id}`}>
      <Card className="overflow-hidden hover-elevate transition-all duration-300 hover:shadow-lg group">
        <div className="relative aspect-[4/3] overflow-hidden bg-muted">
          <img
            src={getOptimizedImageUrl(primaryImage, 900)}
            srcSet={generateSrcSet()}
            sizes={imageSizes}
            alt={property.title}
            title={property.title}
            loading={priority ? "eager" : "lazy"}
            decoding={priority ? "sync" : "async"}
            width={400}
            height={300}
            onError={(e) => {
              e.currentTarget.onerror = null
              e.currentTarget.src = "/og-image.jpg"
            }}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          />

          <div className="absolute top-3 left-3">
            <Badge
              variant={getListingBadgeVariant(property.listingType)}
              className="text-xs font-semibold uppercase"
              data-testid={`badge-listing-type-${property.id}`}
            >
              For {property.listingType}
            </Badge>
          </div>

          {property.featured && (
            <div className="absolute top-3 right-3">
              <Badge
                className="bg-yellow-700 text-white hover:bg-yellow-800 flex items-center gap-1 font-semibold"
                data-testid={`badge-featured-${property.id}`}
              >
                <Star className="h-3 w-3 fill-white" aria-hidden="true" />
                Featured
              </Badge>
            </div>
          )}

          <div className="absolute bottom-3 left-3">
            <Badge
              className="bg-primary text-primary-foreground text-sm font-bold px-3 py-1"
              data-testid={`badge-price-${property.id}`}
            >
              {formatPrice(property.price)}
              {property.listingType === "rent" && "/mo"}
            </Badge>
          </div>
        </div>

        <div className="p-4">
          <h3
            className="font-semibold text-lg mb-2 line-clamp-1"
            data-testid={`text-title-${property.id}`}
          >
            {property.title}
          </h3>

          <div className="flex items-start gap-1 text-muted-foreground mb-3">
            <MapPin
              className="h-4 w-4 flex-shrink-0 mt-0.5"
              aria-hidden="true"
            />
            <span
              className="text-sm line-clamp-2"
              data-testid={`text-location-${property.id}`}
            >
              {formatAddress()}
            </span>
          </div>

          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            {property.bedrooms > 0 && (
              <div
                className="flex items-center gap-1"
                data-testid={`text-bedrooms-${property.id}`}
                title={`${property.bedrooms} bedrooms`}
              >
                <Bed className="h-4 w-4" aria-hidden="true" />
                <span>{property.bedrooms}</span>
              </div>
            )}
            {property.bathrooms > 0 && (
              <div
                className="flex items-center gap-1"
                data-testid={`text-bathrooms-${property.id}`}
                title={`${property.bathrooms} bathrooms`}
              >
                <Bath className="h-4 w-4" aria-hidden="true" />
                <span>{property.bathrooms}</span>
              </div>
            )}
            {property.floorArea && (
              <div
                className="flex items-center gap-1"
                data-testid={`text-floor-area-${property.id}`}
                title={`${property.floorArea} square meters`}
              >
                <Maximize className="h-4 w-4" aria-hidden="true" />
                <span>{property.floorArea} sqm</span>
              </div>
            )}
          </div>

          <div className="mt-3 flex items-center justify-between gap-2">
            <Badge
              variant="outline"
              className="text-xs capitalize"
              data-testid={`badge-type-${property.id}`}
            >
              {property.propertyType}
            </Badge>
            <Badge
              className="bg-primary text-primary-foreground text-xs font-bold px-2 py-1 hover:bg-primary"
              data-testid={`badge-price-bottom-${property.id}`}
            >
              {formatPrice(property.price)}
              {property.listingType === "rent" && "/mo"}
            </Badge>
          </div>
        </div>
      </Card>
    </Link>
  )
}
